import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DineOutletAdmin, DineOutletMealPeriod, SaveDineOutletMealPeriod } from 'api/types';

/**
 * The meals one outlet serves and how (buffet check-off slice 2, decision V1):
 * buffet (the waiter app shows the check-off board), à la carte, or not served,
 * by weekday and date range. A row with dates overrides the everyday row on
 * those dates — a temporary move (renovation, season) is a dated row. Several
 * outlets may serve the same meal; a guest's allowance is still one.
 */
interface Props {
  isOpen: boolean;
  outlet: DineOutletAdmin | null;
  canEdit: boolean;
  onClose: () => void;
  /**
   * Shown as a section of the outlet window (DineOpsOutletWorkspace) rather than as its own window:
   * no backdrop, header or close; saving stays in the section. Default: its own window.
   */
  embedded?: boolean;
  /** Embedded: unsaved edits in this section, so the outlet window can warn before closing. */
  onDirtyChange?: (dirty: boolean) => void;
  /** After a meal period is saved or removed, so the outlet list's Buffet column can refresh. */
  onSaved?: () => void;
}

const MEALS = [
  { value: 'BREAKFAST', label: 'Breakfast' },
  { value: 'LUNCH', label: 'Lunch' },
  { value: 'DINNER', label: 'Dinner' },
];
/**
 * Buffet board Yes / No. Nothing here limits ordering: à la carte is always open while the
 * outlet is. No is stored as A_LA_CARTE; NOT_SERVED (older rows) reads as No too.
 */
const STYLES = [
  { value: 'BUFFET', label: 'Yes' },
  { value: 'A_LA_CARTE', label: 'No' },
];
const styleLabel = (v: string) => (v === 'BUFFET' ? 'Yes' : 'No');
/** Monday 1 … Sunday 64, as the API stores them. */
const DAYS = [
  { bit: 1, label: 'Mon' }, { bit: 2, label: 'Tue' }, { bit: 4, label: 'Wed' }, { bit: 8, label: 'Thu' },
  { bit: 16, label: 'Fri' }, { bit: 32, label: 'Sat' }, { bit: 64, label: 'Sun' },
];
const EVERY_DAY = 127;

interface FormState {
  id: string | null;
  mealPeriod: string;
  serviceStyle: string;
  windowFrom: string;
  windowTo: string;
  daysMask: number;
  effectiveFrom: string;
  effectiveTo: string;
  remarks: string;
  /** Buffet price outside the plan (slice 3); '' = not sold outside the plan / children at the adult rate. */
  adultRate: string;
  childRate: string;
  rowVersion: number;
}

const EMPTY_FORM: FormState = {
  id: null, mealPeriod: 'BREAKFAST', serviceStyle: 'BUFFET', windowFrom: '', windowTo: '', daysMask: EVERY_DAY,
  effectiveFrom: '', effectiveTo: '', remarks: '', adultRate: '', childRate: '', rowVersion: 0,
};

const money = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** What guests outside the plan pay, as the list shows it. */
function rateText(r: DineOutletMealPeriod): string {
  if (r.serviceStyle !== 'BUFFET') return '—';
  if (r.adultRate == null) return 'Plan guests only';
  return `Adult ${money(r.adultRate)} · Child ${money(r.childRate ?? r.adultRate)}`;
}

export function daysText(mask: number): string {
  if (mask === EVERY_DAY) return 'Every day';
  if (mask === 31) return 'Mon–Fri';
  if (mask === 96) return 'Sat, Sun';
  return DAYS.filter((d) => (mask & d.bit) !== 0).map((d) => d.label).join(', ');
}

function dateText(from: string | null, to: string | null): string {
  const f = (v: string) => new Date(v).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  if (!from && !to) return '—';
  if (from && to) return `${f(from)} – ${f(to)}`;
  return from ? `From ${f(from)}` : `Until ${f(to!)}`;
}

const label = (list: { value: string; label: string }[], v: string) => list.find((x) => x.value === v)?.label ?? v;

const DineOpsOutletMealPeriodsModal: React.FC<Props> = ({ isOpen, outlet, canEdit, onClose, embedded = false, onDirtyChange, onSaved }) => {
  const [rows, setRows] = useState<DineOutletMealPeriod[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [snapshot, setSnapshot] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [removing, setRemoving] = useState<DineOutletMealPeriod | null>(null);
  /** Children under this age eat a paid buffet free (slice 3); the saved value and the one being typed. */
  const [freeAge, setFreeAge] = useState(5);
  const [freeAgeInput, setFreeAgeInput] = useState('5');

  const load = useCallback(async () => {
    if (!outlet) return;
    setLoading(true);
    try {
      const [periods, settings] = await Promise.all([
        dineOpsService.getOutletMealPeriods(outlet.id),
        dineOpsService.getOutletBuffetSettings(outlet.id),
      ]);
      setFreeAge(settings.childFreeAge);
      setFreeAgeInput(String(settings.childFreeAge));
      setRows(periods);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'The meal periods could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [outlet]);

  useEffect(() => {
    if (!isOpen) return;
    setForm(null);
    setSnapshot('');
    setError(null);
    void load();
  }, [isOpen, load]);

  const isDirty = !!form && JSON.stringify(form) !== snapshot;
  useEffect(() => { onDirtyChange?.(isDirty); }, [isDirty, onDirtyChange]);

  const openForm = (row: DineOutletMealPeriod | null) => {
    const next: FormState = row
      ? {
          id: row.id, mealPeriod: row.mealPeriod, serviceStyle: row.serviceStyle === 'BUFFET' ? 'BUFFET' : 'A_LA_CARTE', windowFrom: row.windowFrom ?? '',
          windowTo: row.windowTo ?? '', daysMask: row.daysMask, effectiveFrom: row.effectiveFrom?.slice(0, 10) ?? '',
          effectiveTo: row.effectiveTo?.slice(0, 10) ?? '', remarks: row.remarks ?? '',
          adultRate: row.adultRate == null ? '' : String(row.adultRate), childRate: row.childRate == null ? '' : String(row.childRate),
          rowVersion: row.rowVersion,
        }
      : { ...EMPTY_FORM };
    setForm(next);
    setSnapshot(JSON.stringify(next));
    setError(null);
  };

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  /** What the API would refuse, said here first; null when the form can be saved. */
  const formError = useMemo(() => {
    if (!form) return null;
    if (!!form.windowFrom !== !!form.windowTo) return 'Give both the start and the end of the usual time, or neither.';
    if (form.windowFrom && form.windowFrom >= form.windowTo) return 'The usual time must end after it starts.';
    if (form.daysMask === 0) return 'Choose at least one day.';
    if (form.effectiveFrom && form.effectiveTo && form.effectiveFrom > form.effectiveTo) return "The 'from' date must be on or before the 'to' date.";
    if (form.serviceStyle === 'BUFFET') {
      const bad = (v: string) => v.trim() !== '' && (Number.isNaN(Number(v)) || Number(v) < 0);
      if (bad(form.adultRate) || bad(form.childRate)) return 'A buffet rate is a number, 0 or more.';
      if (form.childRate.trim() && !form.adultRate.trim()) return 'Set the adult rate before a child rate.';
    }
    return null;
  }, [form]);

  const save = async () => {
    if (!outlet || !form) return;
    setSaveConfirmOpen(false);
    setSaving(true);
    setError(null);
    const body: SaveDineOutletMealPeriod = {
      mealPeriod: form.mealPeriod, serviceStyle: form.serviceStyle,
      windowFrom: form.windowFrom || null, windowTo: form.windowTo || null, daysMask: form.daysMask,
      effectiveFrom: form.effectiveFrom || null, effectiveTo: form.effectiveTo || null,
      remarks: form.remarks.trim() || null, rowVersion: form.rowVersion,
      // Rates belong to a buffet meal only.
      adultRate: form.serviceStyle === 'BUFFET' && form.adultRate.trim() ? Number(form.adultRate) : null,
      childRate: form.serviceStyle === 'BUFFET' && form.childRate.trim() ? Number(form.childRate) : null,
    };
    try {
      if (form.id) await dineOpsService.updateOutletMealPeriod(outlet.id, form.id, body);
      else await dineOpsService.createOutletMealPeriod(outlet.id, body);
      notificationService.toast.success(`${label(MEALS, form.mealPeriod)} saved for "${outlet.outletName}".`);
      setForm(null);
      setSnapshot('');
      await load();
      onSaved?.();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'The meal period could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const freeAgeValue = Number(freeAgeInput);
  const freeAgeValid = freeAgeInput.trim() !== '' && Number.isInteger(freeAgeValue) && freeAgeValue >= 0 && freeAgeValue <= 17;

  const saveFreeAge = async () => {
    if (!outlet || !freeAgeValid) return;
    setSaving(true);
    try {
      const saved = await dineOpsService.saveOutletBuffetSettings(outlet.id, { childFreeAge: freeAgeValue });
      setFreeAge(saved.childFreeAge);
      notificationService.toast.success(saved.childFreeAge === 0
        ? 'Children now pay at the buffet.'
        : `Children under ${saved.childFreeAge} now eat free at the buffet.`);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'The free age could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!outlet || !removing) return;
    const row = removing;
    setRemoving(null);
    setSaving(true);
    try {
      await dineOpsService.deleteOutletMealPeriod(outlet.id, row.id, row.rowVersion);
      notificationService.toast.success(`${label(MEALS, row.mealPeriod)} row removed.`);
      await load();
      onSaved?.();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'The meal period could not be removed.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !outlet) return null;

  const cell: React.CSSProperties = { padding: '6px 10px', borderBottom: '1px solid var(--border-color)', fontSize: 13, verticalAlign: 'top' };

  const overlayEl = (saving || loading) && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{saving ? 'Saving...' : 'Loading...'}</span>
          </div>
        );
  const body = (
    <>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 0 }}>
          À la carte orders are always available while the outlet is open. <strong>Buffet board: Yes</strong> adds the
          buffet check-off board for plan guests in the waiter app for that meal. A row with dates replaces the everyday
          row on those dates, e.g. <em>No</em> while breakfast is served in the banquet hall during renovation.
          A buffet meal with an adult rate is also sold to guests outside the plan, billed from the board.
        </p>

        <div data-free-age style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12, fontSize: 13 }}>
          <span>Children eat a paid buffet free under age</span>
          <input type="number" min={0} max={17} className="form-input" style={{ width: 80 }} value={freeAgeInput}
            disabled={!canEdit} aria-label="Free age" onChange={(e) => setFreeAgeInput(e.target.value)} />
          {canEdit && (
            <button className="btn btn-secondary btn-sm" onClick={() => void saveFreeAge()}
              disabled={saving || !freeAgeValid || freeAgeValue === freeAge}>Save</button>
          )}
          <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
            {freeAgeValid ? (freeAgeValue === 0 ? '0 = every child pays.' : 'They go on the bill at 0.00 and count as covers.') : 'A whole number from 0 to 17.'}
          </span>
        </div>

        <div style={{ border: '1px solid var(--border-color)', borderRadius: 6, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }} data-meal-periods>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)', textAlign: 'left' }}>
                <th style={cell}>Meal</th><th style={cell}>Buffet board</th><th style={cell}>Usual time</th>
                <th style={cell}>Days</th><th style={cell}>Dates</th><th style={cell}>Outside the plan</th><th style={cell}>Remarks</th>
                {canEdit && <th style={{ ...cell, width: 120 }} />}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td style={{ ...cell, color: 'var(--text-muted)' }} colSpan={canEdit ? 8 : 7}>
                  No meal periods yet: this outlet has no buffet board (à la carte ordering is not affected).
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id}>
                  <td style={cell}><strong>{label(MEALS, r.mealPeriod)}</strong></td>
                  <td style={cell}>{styleLabel(r.serviceStyle)}</td>
                  <td style={cell}>{r.windowFrom ? `${r.windowFrom}–${r.windowTo}` : '—'}</td>
                  <td style={cell}>{daysText(r.daysMask)}</td>
                  <td style={cell}>{dateText(r.effectiveFrom, r.effectiveTo)}</td>
                  <td style={cell} data-rates>{rateText(r)}</td>
                  <td style={{ ...cell, color: 'var(--text-secondary)' }}>{r.remarks ?? ''}</td>
                  {canEdit && (
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openForm(r)} disabled={!!form}>Edit</button>{' '}
                      <button className="btn btn-danger btn-sm" onClick={() => setRemoving(r)} disabled={!!form}>Remove</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {canEdit && !form && (
          <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={() => openForm(null)}>
            <IconSystem.add size={16} /> Add meal period
          </button>
        )}

        {form && (
          <div style={{ marginTop: 16, padding: 12, border: '1px solid var(--border-color)', borderRadius: 6 }} data-meal-period-form>
            <h3 style={{ fontSize: 14, margin: '0 0 8px' }}>{form.id ? 'Edit meal period' : 'New meal period'}</h3>
            <div className="form-grid-3">
              <div className="form-group">
                <SyutSelect label="Meal" required options={MEALS} value={form.mealPeriod} isClearable={false} size="md"
                  menuPortalTarget={document.body}
                  onChange={(v) => setForm((f) => f && { ...f, mealPeriod: (v as string) || 'BREAKFAST' })} />
              </div>
              <div className="form-group">
                <SyutSelect label="Buffet board" required options={STYLES} value={form.serviceStyle} isClearable={false} size="md"
                  menuPortalTarget={document.body}
                  onChange={(v) => setForm((f) => f && { ...f, serviceStyle: (v as string) || 'BUFFET' })} />
              </div>
              <div className="form-group">
                <label className="form-label">Usual time</label>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="time" className="form-input" value={form.windowFrom} aria-label="From"
                    onChange={(e) => setForm((f) => f && { ...f, windowFrom: e.target.value })} />
                  <span>–</span>
                  <input type="time" className="form-input" value={form.windowTo} aria-label="To"
                    onChange={(e) => setForm((f) => f && { ...f, windowTo: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Days</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {DAYS.map((d) => {
                  const on = (form.daysMask & d.bit) !== 0;
                  return (
                    <button key={d.bit} type="button" aria-pressed={on}
                      className={`btn btn-sm ${on ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setForm((f) => f && { ...f, daysMask: f.daysMask ^ d.bit })}>
                      {d.label}
                    </button>
                  );
                })}
                <button type="button" className="btn btn-sm btn-secondary"
                  onClick={() => setForm((f) => f && { ...f, daysMask: EVERY_DAY })}>Every day</button>
              </div>
            </div>
            {form.serviceStyle === 'BUFFET' && (
              <div className="form-grid-3" data-rate-fields>
                <div className="form-group">
                  <label className="form-label">Adult rate (outside the plan)</label>
                  <input type="number" min={0} step="0.01" className="form-input" value={form.adultRate} placeholder="Blank: plan guests only"
                    onChange={(e) => setForm((f) => f && { ...f, adultRate: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Child rate</label>
                  <input type="number" min={0} step="0.01" className="form-input" value={form.childRate} placeholder="Blank: the adult rate"
                    onChange={(e) => setForm((f) => f && { ...f, childRate: e.target.value })} />
                </div>
                <div className="form-group" style={{ fontSize: 12, color: 'var(--text-secondary)', alignSelf: 'end' }}>
                  For in-house guests beyond their plan and walk-ins, billed from the board. Taxed as restaurant service on the
                  outlet&rsquo;s price basis (Tax Profile). A weekend price is another row for those days.
                </div>
              </div>
            )}
            <div className="form-grid-3">
              <div className="form-group">
                <label className="form-label">From date (optional)</label>
                <input type="date" className="form-input" value={form.effectiveFrom}
                  onChange={(e) => setForm((f) => f && { ...f, effectiveFrom: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">To date (optional)</label>
                <input type="date" className="form-input" value={form.effectiveTo}
                  onChange={(e) => setForm((f) => f && { ...f, effectiveTo: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Remarks</label>
                <input type="text" className="form-input" value={form.remarks} maxLength={500}
                  placeholder="e.g. In the banquet hall during renovation"
                  onChange={(e) => setForm((f) => f && { ...f, remarks: e.target.value })} />
              </div>
            </div>
            {(formError || error) && <div className="form-error-message" style={{ marginBottom: 8 }}>{formError ?? error}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { setForm(null); setSnapshot(''); setError(null); }} disabled={saving}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={() => setSaveConfirmOpen(true)}
                disabled={saving || !!formError || (!!form.id && !isDirty)}
                title={form.id && !isDirty ? 'No changes to save' : undefined}>
                Save meal period
              </button>
            </div>
          </div>
        )}
    </>
  );
  const dialogs = (
    <>
    <ConfirmDialog open={saveConfirmOpen} title="Confirm Save"
      message={form ? `Save ${label(MEALS, form.mealPeriod).toLowerCase()} with buffet board ${styleLabel(form.serviceStyle).toLowerCase()}, ${daysText(form.daysMask).toLowerCase()}, for "${outlet.outletName}"?` : ''}
      confirmLabel="Save" cancelLabel="Cancel" variant="default" onConfirm={() => void save()} onCancel={() => setSaveConfirmOpen(false)} />
    <ConfirmDialog open={!!removing} title="Remove Meal Period?"
      message={removing ? `Remove the ${label(MEALS, removing.mealPeriod).toLowerCase()} row (${daysText(removing.daysMask)}, ${dateText(removing.effectiveFrom, removing.effectiveTo)})? Marks already made are kept.` : ''}
      confirmLabel="Remove" cancelLabel="Cancel" variant="danger" onConfirm={() => void remove()} onCancel={() => setRemoving(null)} />
    <ConfirmDialog open={discardConfirmOpen} title="Discard Changes?" message="You have unsaved changes. Do you want to discard them?"
      confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
      onConfirm={() => { setDiscardConfirmOpen(false); setForm(null); setSnapshot(''); onClose(); }} onCancel={() => setDiscardConfirmOpen(false)} />
    </>
  );

  if (embedded) {
    return (
      <div className="outlet-ws-pane">
        {overlayEl}
        {body}
        {dialogs}
      </div>
    );
  }

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        {overlayEl}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Meal Periods — {outlet.outletName} ({outlet.outletCode})</h2>
          <button className="modal-close" onClick={handleClose} disabled={saving}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {body}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Close</button>
        </div>
      </div>
      {dialogs}
    </>
  );
};

export default DineOpsOutletMealPeriodsModal;
